import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, RotateCcw, CheckCircle2, AlertCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inp, pill, ROBOTS, Field, GooglePreview, Score } from "./shared";

const FIELDS = [["title", "SEO Title", 60], ["description", "Meta Description", 160, true], ["keywords", "Meta Keywords (optional)"],
  ["canonical", "Canonical URL"], ["og_title", "Open Graph Title"], ["og_description", "Open Graph Description", 200, true],
  ["og_image", "Open Graph Image URL"], ["twitter_title", "Twitter/X Title"], ["twitter_description", "Twitter/X Description", 200, true],
  ["twitter_image", "Twitter/X Image URL"]];
const SCHEMA = ["", "WebSite", "WebPage", "CollectionPage", "Book", "Person", "Article", "AboutPage", "ContactPage"];

export default function PageEditor({ pageKey, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/seo/page", { params: { key: pageKey } }).then((r) => { setD(r.data); setF({ ...r.data.override, slug: r.data.slug }); });
  useEffect(() => { load(); }, [pageKey]); // eslint-disable-line
  if (!d) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const auto = (k) => d.auto?.[k] || d.effective?.[k] || "";
  const save = async () => {
    setBusy(true);
    try {
      const body = {}; FIELDS.forEach(([k]) => { body[k] = f[k] ?? ""; });
      Object.assign(body, { robots: f.robots || "", schema_type: f.schema_type || "", sitemap_exclude: !!f.sitemap_exclude });
      if (d.slug) body.slug = f.slug;
      const { data } = await api.put("/admin/seo/page", body, { params: { key: pageKey } });
      setD(data); setF({ ...data.override, slug: data.slug }); toast.success("SEO saved & live"); onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const reset = async () => {
    if (!window.confirm("Reset this page to auto-generated SEO? Your manual values will be removed.")) return;
    await api.delete("/admin/seo/page", { params: { key: pageKey } }); toast.success("Reset to auto"); load(); onSaved?.();
  };
  const eff = d.effective;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end" data-testid="seo-page-editor">
      <div className="w-full max-w-3xl h-full overflow-y-auto bg-background border-l border-border p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div><p className="track-label text-[11px] text-muted-foreground">{d.type} · {d.path}</p><h2 className="font-display text-2xl font-bold">{d.name}</h2></div>
          <div className="flex items-center gap-2"><Score v={d.score} /><button onClick={onClose} data-testid="seo-editor-close"><X className="h-5 w-5" /></button></div>
        </div>
        <GooglePreview title={f.title || eff.title} url={f.canonical || eff.canonical} description={f.description || eff.description} />
        <div className="grid md:grid-cols-2 gap-2" data-testid="seo-checklist">
          {d.checklist.map((c) => (
            <p key={c.id} className={`text-xs flex items-start gap-1.5 ${c.ok ? "text-emerald-400" : "text-amber-400"}`} data-testid={`seo-check-${c.id}`}>
              {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}{c.label}
            </p>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Leave a field empty to use the auto-generated value (shown as placeholder). Checks are suggestions — nothing changes until you save.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {FIELDS.map(([k, label, max, area]) => (
            <div key={k} className={area || k === "title" ? "md:col-span-2" : ""}>
              <Field label={label} hint={max ? `${(f[k] || auto(k)).length}/${max}` : ""}>
                {area ? <textarea rows={2} className={inp} value={f[k] || ""} placeholder={auto(k)} onChange={set(k)} data-testid={`seo-field-${k}`} />
                  : <input className={inp} value={f[k] || ""} placeholder={auto(k)} onChange={set(k)} data-testid={`seo-field-${k}`} />}
              </Field>
            </div>
          ))}
          {d.slug && <Field label="URL Slug" hint="Changing it keeps the old URL working (auto-redirect)"><input className={inp} value={f.slug || ""} onChange={set("slug")} data-testid="seo-field-slug" /></Field>}
          <Field label="Robots Meta"><select className={inp} value={f.robots || ""} onChange={set("robots")} data-testid="seo-field-robots">
            <option value="">Default ({eff.robots})</option>{ROBOTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Schema Type"><select className={inp} value={f.schema_type || ""} onChange={set("schema_type")} data-testid="seo-field-schema">
            {SCHEMA.map((s) => <option key={s} value={s}>{s || `Auto (${d.auto.schema_type})`}</option>)}</select></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.sitemap_exclude} onChange={set("sitemap_exclude")} data-testid="seo-field-sitemap-exclude" /> Exclude from sitemap</label>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className={`${pill} bg-primary text-primary-foreground`} data-testid="seo-editor-save">{busy ? "Saving…" : "Save & Publish"}</button>
          {d.custom && <button onClick={reset} className={`${pill} border border-border`} data-testid="seo-editor-reset"><RotateCcw className="h-4 w-4" /> Reset to auto</button>}
        </div>
        <div>
          <p className="track-label text-[11px] text-muted-foreground mb-2">Structured data preview</p>
          <pre className="text-[11px] bg-secondary/50 rounded-xl p-3 overflow-x-auto max-h-72" data-testid="seo-schema-preview">{JSON.stringify(d.schema, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}
